import { addHours, differenceInMinutes } from "date-fns";
import { FlightCardTypes } from "../../enum/flights/FlightEnum";
import {
  FlightKPIUrlsAndFilterHours,
  FlightKPIUrlsType,
  KPISettingsIDs,
} from "../../enum/FlightsEnum";
import { GlobalFilterOptionsEnum } from "../../enum/global-filter/GlobalFilterEnum";
import { IKpiSettingsAPI } from "../../models/app-settings/AppSettingsModels";
import { IGlobalDateFilter } from "../../models/filters/IFilters";
import { GroundHandlerType } from "../../models/flights/IFlightsCommon";
import { convertTimeStringToHourMinute } from "../app-settings/AppSettingsHelper";
import { getApiUrl } from "../common/CommonHelperFunctions";
import { BadgeStatusType } from "../common/Constants";
import { convertToRollingHours } from "../common/DateHelperFunctions";
import { StandStatusTypes } from "./FlightConstants";

/**
 * Generates updated KPI data URLs and filter hours based on the provided parameters.
 *
 */
export const getUpdatedKpiDataUrls = (
  airportCode: string,
  timezone: string,
  currentTime: Date,
  globalFilterDate: IGlobalDateFilter,
  globalFilterOption: GlobalFilterOptionsEnum,
  kpiSettings: IKpiSettingsAPI[],
  flightKpisUrls: FlightKPIUrlsType
): FlightKPIUrlsAndFilterHours => {
  const flightKpiBaseUrlTemplate = process.env.REACT_APP_FLIGHT_KPI;
  const urlIDs = Object.keys(flightKpisUrls) as Array<keyof FlightKPIUrlsType>;

  const totalMinutes = differenceInMinutes(
    currentTime,
    globalFilterDate.startDate
  );
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return urlIDs.reduce((acc, urlID) => {
    //Default values for today option or inactive rolling filter KPIs
    let startDateTime = globalFilterDate.startDate;
    let endDateTime = globalFilterDate.endDate;
    let startHours = hours;
    let startMinutes = minutes;

    const kpiType = flightKpisUrls[urlID];
    const kpiFilterValue = kpiSettings.find((kpi) => kpi.name === urlID);

    if (!kpiType || !flightKpiBaseUrlTemplate) {
      acc[urlID] = {
        url: undefined,
        filterTime: { hours: 0, minutes: 0 },
        filterDate: globalFilterDate,
      };
      return acc;
    }

    if (
      globalFilterOption === GlobalFilterOptionsEnum.rolling &&
      kpiFilterValue?.isRollingActive
    ) {
      const { hours: fromHour, minutes: fromMinute } =
        convertTimeStringToHourMinute(kpiFilterValue.from);
      const { hours: toHour, minutes: toMinute } =
        convertTimeStringToHourMinute(kpiFilterValue.to);

      const addFromHours = convertToRollingHours(fromHour, fromMinute);
      const addToHours = convertToRollingHours(toHour, toMinute);

      startDateTime = addHours(currentTime, addFromHours);
      endDateTime = addHours(currentTime, addToHours);
      startHours = Math.abs(fromHour);
      startMinutes = fromMinute;
    }

    const urlTemplate = flightKpiBaseUrlTemplate.replace("{kpiType}", kpiType);

    const url = getApiUrl(
      urlTemplate,
      airportCode,
      startDateTime,
      endDateTime,
      timezone
    );

    acc[urlID] = {
      url,
      filterTime: { hours: startHours, minutes: startMinutes },
      filterDate: {
        startDate: startDateTime,
        endDate: endDateTime,
      },
    };
    return acc;
  }, {} as FlightKPIUrlsAndFilterHours);
};

/**
 * Determines the flight card type based on the provided value.
 *
 * @param value - A string representing the type of flight card or `null`.
 *                Possible values include:
 *                - `"onTimePerformance"`: Maps to `FlightCardTypes.OTP`.
 *                - `"slotAdherence"`: Maps to `FlightCardTypes.SLOT_ADHERENCE`.
 *                - `"runwayDelay"`: Maps to `FlightCardTypes.RUNWAY_DELAYS`.
 *                - `"turnaroundSufficiency"`: Maps to `FlightCardTypes.TURNAROUND_SUFFICIENCY`.
 *                If the value is `null` or does not match any of the above, it defaults to `FlightCardTypes.SUMMARY`.
 *
 * @returns The corresponding `FlightCardTypes` enum value.
 */
export const getSelectedFlightCardType = (
  value: string | null
): FlightCardTypes => {
  if (!value) {
    return FlightCardTypes.SUMMARY;
  }
  switch (value) {
    case "onTimePerformance":
      return FlightCardTypes.OTP;
    case "slotAdherence":
      return FlightCardTypes.SLOT_ADHERENCE;
    case "runwayDelay":
      return FlightCardTypes.RUNWAY_DELAYS;
    case "turnaroundSufficiency":
      return FlightCardTypes.TURNAROUND_SUFFICIENCY;
    default:
      return FlightCardTypes.SUMMARY;
  }
};

/**
 * Retrieves the corresponding KPISettingsID for a given flight card type.
 *
 * @param flightCardType - The type of the flight card, represented as a value from the `FlightCardTypes` enum.
 * @returns The corresponding `KPISettingsIDs` value if a match is found, or `null` if the flight card type does not match any case.
 *
 */
export const getFlightKpiID = (
  flightCardType: FlightCardTypes
): KPISettingsIDs | null => {
  switch (flightCardType) {
    case FlightCardTypes.OTP:
      return KPISettingsIDs.ON_TIME_PERFORMANCE;
    case FlightCardTypes.SLOT_ADHERENCE:
      return KPISettingsIDs.SLOT_ADHERENCE;
    case FlightCardTypes.RUNWAY_DELAYS:
      return KPISettingsIDs.RUNWAY_DELAY;
    case FlightCardTypes.TURNAROUND_SUFFICIENCY:
      return KPISettingsIDs.TURNAROUND_SUFFICIENCY;
    case FlightCardTypes.ADDITIONAL_DELAY:
      return KPISettingsIDs.ADDITIONAL_DELAY;
    case FlightCardTypes.REGULATED_FLIGHT:
      return KPISettingsIDs.REGULATED_FLIGHT;
    default:
      return null; // Default case if no match is found
  }
};

export const getKPIFilterTime = (
  selectedSection: FlightCardTypes,
  kpiConfigData: FlightKPIUrlsAndFilterHours,
  globalFilterDate: IGlobalDateFilter
): IGlobalDateFilter => {
  if (selectedSection === FlightCardTypes.SUMMARY) {
    return globalFilterDate;
  }
  const kpiId = getFlightKpiID(selectedSection);
  const kpiFilterValue = kpiId && kpiConfigData[kpiId];
  return kpiFilterValue?.filterDate || globalFilterDate;
};

/**
 * Transforms groundHandler object into a formatted display string.
 * Example: { FLT: ["TSA"], FUE: ["IND", "CRU"] } => "FLT: TSA; FUE: IND, CRU"
 */
export const formatGroundHandler = (
  groundHandler: GroundHandlerType | null | undefined
): string => {
  if (!groundHandler || Object.keys(groundHandler).length === 0) {
    {
      return "";
    }
  }

  return Object.entries(groundHandler)
    .filter(([, values]) => values?.length > 0) // Skip empty arrays
    .map(([key, values]) => `${key}: ${values.join(", ")}`)
    .join("; ");
};

export const getFlightStatusBadgeType = (
  status: StandStatusTypes
): BadgeStatusType => {
  switch (status) {
    case "ON_TIME":
    case "EARLY":
      return "success";
    case "DELAYED":
      return "warning";
    case "SCHEDULED":
      return "informative";
    default:
      return "subtle";
  }
};
